/**
 * The Requests tab (masterplan credit-limits-v3, B4; E6, E7, E10, E11): every limit request of Dolder-Bigler in the
 * ClimGrid, newest first, with the filter «Waiting for me». Columns: customer, old and new limit, tier, requested by and
 * at, status with the name of the next person. The action column shows a button for each action the API allows; a
 * click (or Enter on the row) opens the request with its steps and history, where the action is confirmed, with a
 * reason where one is required. The open request is in the URL (`?tab=requests&request=<id>`, the link of the mails).
 */
import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ClimGrid, type ClimColumn } from '../grid';
import type { CreditLimitRequestDto } from '../../../types/creditLimits';
import { formatChf } from '../overview/overviewColumns';
import { useDisplayNamesVersion } from '../config/displayNames';
import { nameFromEmail } from './chainPreview';
import { RequestDialog } from './RequestDialog';
import {
    REQUEST_PARAM, WAITING_PARAM, actionButtons, formatDateTime, isWaitingForMe, requestIdFromParams, statusText,
    visibleRequests, type ActionButton,
} from './requestRules';
import { useCreditLimitRequests, type RequestActionInput } from './useCreditLimitRequests';
import { useCreditLimitConfig } from '../config/useCreditLimitConfig';

interface Props {
    /** Called after an action changed a request, so the overview's «Last request» and the cell lock follow. */
    onChanged?: () => void;
}

const rowKey = (r: CreditLimitRequestDto) => String(r.id);
const PANEL_STYLE = { '--tbl-offset': '300px' } as CSSProperties;

export function requestColumns(open: (r: CreditLimitRequestDto, b: ActionButton | null) => void): readonly ClimColumn<CreditLimitRequestDto>[] {
    return [
        {
            key: 'customer', label: 'Customer', width: 260, isKey: true, hideable: false,
            value: r => r.customerName?.trim() || r.customerCode,
            render: r => (
                <span className="rowkey">
                    <span className="name">{r.customerName?.trim() || r.customerCode}</span>
                    <span className="meta">{r.customerCode}</span>
                </span>
            ),
        },
        { key: 'old', label: 'Old CHF', title: 'Limit before the request (CHF)', width: 120, numeric: true, value: r => r.currentLimitChf, render: r => formatChf(r.currentLimitChf) },
        { key: 'new', label: 'New CHF', title: 'Requested limit (CHF)', width: 120, numeric: true, value: r => r.requestedLimitChf, render: r => <b>{formatChf(r.requestedLimitChf)}</b> },
        { key: 'tier', label: 'Tier', width: 70, numeric: true, value: r => r.tierNo },
        { key: 'by', label: 'Requested by', width: 170, value: r => nameFromEmail(r.requestedBy) },
        { key: 'at', label: 'Requested at', width: 150, value: r => r.requestedAt, render: r => formatDateTime(r.requestedAt), filterable: false },
        { key: 'status', label: 'Status', title: 'Status and the person who decides next', width: 320, value: r => statusText(r) },
        {
            key: 'actions', label: 'Actions', width: 300, sortable: false, filterable: false,
            value: r => r.allowedActions.map(a => a.action).join(' '),
            render: r => {
                const buttons = actionButtons(r);
                return (
                    <span style={{ display: 'inline-flex', gap: 6 }}>
                        {buttons.length === 0
                            ? <button type="button" className="lnk" tabIndex={-1} onClick={() => open(r, null)}>Details</button>
                            : buttons.map(b => (
                                <button key={`${b.action}-${b.onBehalf}`} type="button" className={`btn ${b.tone} sm`} tabIndex={-1} onClick={() => open(r, b)}>{b.label}</button>
                            ))}
                    </span>
                );
            },
        },
    ];
}

export function RequestsTab({ onChanged }: Props) {
    const { requests, loading, error, reload, act } = useCreditLimitRequests();
    // Only Finance sees the NAV error text and the lock (Gate B review); a failed config load just leaves both hidden.
    const { config, reload: reloadConfig } = useCreditLimitConfig();
    const isFinance = config?.roles.finance === true;
    const navLocked = isFinance && (!!config?.settings.navWriteLockedAt || !!config?.settings.navAuthFailedAt);
    const [params, setParams] = useSearchParams();
    const waitingOnly = params.get(WAITING_PARAM) === '1';
    const openId = requestIdFromParams(params);
    const [initialAction, setInitialAction] = useState<ActionButton | null>(null);

    const setParam = useCallback((key: string, value: string | null) => {
        setParams(prev => {
            const next = new URLSearchParams(prev);
            if (value === null) next.delete(key); else next.set(key, value);
            return next;
        }, { replace: true });
    }, [setParams]);

    const open = useCallback((r: CreditLimitRequestDto, b: ActionButton | null) => {
        setInitialAction(b);
        setParam(REQUEST_PARAM, String(r.id));
    }, [setParam]);
    const close = () => { setInitialAction(null); setParam(REQUEST_PARAM, null); };

    const rows = useMemo(() => visibleRequests(requests ?? [], waitingOnly), [requests, waitingOnly]);
    const waitingCount = useMemo(() => (requests ?? []).filter(isWaitingForMe).length, [requests]);
    // The names arrive with `GET config` (B5), possibly after the list: rebuild the columns when they do.
    const namesVersion = useDisplayNamesVersion();
    const columns = useMemo(() => requestColumns(open), [open, namesVersion]);
    const current = openId !== null && requests ? requests.find(r => r.id === openId) ?? null : null;

    const onAct = async (id: number, body: RequestActionInput) => {
        let next: CreditLimitRequestDto;
        try {
            next = await act(id, body);
        } catch (err) {
            // 423: NAV writing was locked meanwhile - read the lock again so the dialog shows it.
            if ((err as { status?: number } | null)?.status === 423) reloadConfig();
            throw err;
        }
        onChanged?.();
        return next;
    };

    const toolbar = (
        <button type="button" className="btn secondary sm" aria-pressed={waitingOnly} onClick={() => setParam(WAITING_PARAM, waitingOnly ? null : '1')}>
            Waiting for me{waitingCount > 0 ? <span className="cnt">{waitingCount}</span> : null}
        </button>
    );

    return (
        <div className="clim-panel" style={PANEL_STYLE}>
            {error && (
                <div className="banner error" role="alert">
                    <p>{error}</p>
                    <div className="actions"><button type="button" className="btn secondary sm" onClick={reload}>Retry</button></div>
                </div>
            )}
            {loading && !requests && <p className="muted" role="status">Loading requests…</p>}
            {openId !== null && requests && !current && (
                <div className="banner attention" role="status">
                    <p>Request #{openId} was not found.</p>
                    <div className="actions"><button type="button" className="btn secondary sm" onClick={close}>Dismiss</button></div>
                </div>
            )}
            {requests && (
                <ClimGrid<CreditLimitRequestDto>
                    gridId="credit-limits-requests"
                    caption="Credit limit requests"
                    columns={columns}
                    rows={rows}
                    rowKey={rowKey}
                    defaultSort={{ key: 'at', direction: 'desc' }}
                    onRowActivate={r => open(r, null)}
                    selectedKey={current ? rowKey(current) : null}
                    toolbar={toolbar}
                    labels={{
                        count: shown => `${shown.toLocaleString('de-CH')} of ${requests.length.toLocaleString('de-CH')} requests`,
                        empty: waitingOnly ? 'No request is waiting for you.' : 'No requests yet.',
                    }}
                />
            )}
            {current && <RequestDialog key={current.id} request={current} onAct={onAct} onClose={close} initialAction={initialAction} isFinance={isFinance} navLocked={navLocked} />}
        </div>
    );
}
